/**
 * Human-readable labels for site audit findings.
 *
 * Each entry maps an `issueKey` (or issueKey prefix for dynamic keys) to a
 * short title and optional one-line description suitable for UI display,
 * PDF/PPT exports, and tooltips.
 *
 * The `resolveFindingLabel` function at the bottom handles the lookup with
 * fallback logic so callers get a clean title + description every time.
 */

export interface FindingLabel {
  title: string;
  description: string;
}

// ─── Static registry keyed by exact issueKey ────────────────────────────────

const FINDING_LABELS: Record<string, FindingLabel> = {
  // ── Freshness ───────────────────────────────────────────────────────────
  "freshness.jsonLd.bothPresent": {
    title: "JSON-LD dates present",
    description: "Both datePublished and dateModified are set in structured data.",
  },
  "freshness.jsonLd.publishedOnly": {
    title: "JSON-LD missing dateModified",
    description: "Only datePublished is present — adding dateModified helps AI systems know when content was last updated.",
  },
  "freshness.jsonLd.missing": {
    title: "JSON-LD dates missing",
    description: "No datePublished or dateModified found in structured data. AI systems cannot determine content freshness.",
  },
  "freshness.jsonLd.modifiedBeforePublished": {
    title: "Date inconsistency",
    description: "dateModified is earlier than datePublished, which signals a data error to AI systems.",
  },
  "freshness.jsonLd.futureDate": {
    title: "Future date detected",
    description: "A date in the structured data is set in the future, which may be ignored by AI systems.",
  },
  "freshness.jsonLd.ancientDate": {
    title: "Very old date detected",
    description: "A date in the structured data is extremely old, which may reduce AI confidence in content relevance.",
  },
  "freshness.og.present": {
    title: "Open Graph dates present",
    description: "article:published_time or article:modified_time meta tags are set.",
  },
  "freshness.og.missing": {
    title: "Open Graph dates missing",
    description: "No article:published_time or article:modified_time meta tags found.",
  },
  "freshness.http.present": {
    title: "HTTP Last-Modified present",
    description: "The server sends a Last-Modified header, giving AI crawlers a freshness signal.",
  },
  "freshness.http.missing": {
    title: "HTTP Last-Modified missing",
    description: "No Last-Modified header — AI crawlers have one fewer signal for content freshness.",
  },
  "freshness.consistency.jsonLdOgMismatch": {
    title: "Date mismatch between JSON-LD and Open Graph",
    description: "The dates in structured data and Open Graph tags disagree, which can confuse AI systems.",
  },
  "freshness.consistency.httpStale": {
    title: "HTTP date conflicts with structured data",
    description: "The server's Last-Modified header disagrees significantly with JSON-LD dates.",
  },

  // ── Chunk Quality ───────────────────────────────────────────────────────
  "chunkQuality.structuralDesert": {
    title: "Structural desert detected",
    description: "Long stretches of content lack headings, lists, or paragraph breaks, making it harder for AI to extract coherent chunks.",
  },
  "chunkQuality.windowAlignment.info": {
    title: "Window alignment (informational)",
    description: "Shows how many token windows start at natural content boundaries. This is for visualization only and does not affect the score.",
  },
  "chunkQuality.bestChunk.self-contained": {
    title: "Best chunk: self-contained",
    description: "The token window with the highest topic keyword overlap reads as a complete, standalone passage.",
  },
  "chunkQuality.bestChunk.partial": {
    title: "Best chunk: partially complete",
    description: "The token window with the highest topic keyword overlap starts or ends mid-thought and may not summarize well in isolation.",
  },
  "chunkQuality.bestChunk.fragmented": {
    title: "Best chunk: fragmented",
    description: "The token window with the highest topic keyword overlap is broken across ideas — restructuring content around clear topics would help.",
  },
  "chunkQuality.overall.green": {
    title: "Chunk quality: good",
    description: "Content is well-structured for AI tokenization and extraction.",
  },
  "chunkQuality.overall.yellow": {
    title: "Chunk quality: needs improvement",
    description: "Content structure could be improved to produce better AI-readable chunks.",
  },
  "chunkQuality.overall.red": {
    title: "Chunk quality: poor",
    description: "Content lacks the structural markers AI systems need to extract clean, coherent passages.",
  },

  // ── Performance ─────────────────────────────────────────────────────────
  "performance.ttfb.slow": {
    title: "Slow time to first byte",
    description: "Server response is slow (>1s TTFB), which may cause AI crawlers to timeout or deprioritize this page.",
  },
  "performance.ttfb.moderate": {
    title: "Moderate time to first byte",
    description: "Server response time is acceptable but could be faster for optimal AI crawling.",
  },
  "performance.ttfb.good": {
    title: "Fast time to first byte",
    description: "Server responds quickly, ensuring AI crawlers can efficiently fetch content.",
  },
  "performance.transferTime.slow": {
    title: "Slow HTML transfer",
    description: "HTML takes a long time to fully download, which may affect AI crawler efficiency.",
  },
  "performance.transferTime.moderate": {
    title: "Moderate HTML transfer time",
    description: "HTML transfer time is acceptable but not optimal.",
  },
  "performance.transferTime.good": {
    title: "Fast HTML transfer",
    description: "HTML downloads quickly, ideal for AI crawler efficiency.",
  },
  "performance.htmlSize.excessive": {
    title: "Excessive HTML size",
    description: "The HTML payload is very large, which increases crawl cost and may hit AI crawler size limits.",
  },
  "performance.htmlSize.large": {
    title: "Large HTML size",
    description: "HTML size is above average — reducing unnecessary markup would improve crawl efficiency.",
  },
  "performance.htmlSize.good": {
    title: "HTML size is efficient",
    description: "The HTML payload is well-sized for AI crawlers.",
  },
  "performance.contentToMarkup.info": {
    title: "Content-to-markup ratio",
    description: "Ratio of meaningful text content to total HTML markup.",
  },
  "performance.fetch.failed": {
    title: "Page fetch failed",
    description: "The page could not be retrieved — AI crawlers will also fail to access this content.",
  },

  // ── Static Content ──────────────────────────────────────────────────────
  "static_content.rendered_empty": {
    title: "Page renders no content",
    description: "Even with JavaScript, the page produced no visible text content.",
  },
  "static_content.static_empty": {
    title: "No content without JavaScript",
    description: "This page has zero content visible without JavaScript. AI assistants cannot see any of this page's content.",
  },
  "static_content.ratio.green": {
    title: "Good static content ratio",
    description: "Most content is available without JavaScript — AI crawlers can see this page clearly.",
  },
  "static_content.ratio.yellow": {
    title: "Partial JavaScript dependency",
    description: "Some content requires JavaScript to appear. AI crawlers may miss these sections.",
  },
  "static_content.ratio.red": {
    title: "Heavy JavaScript dependency",
    description: "Most content requires JavaScript rendering. AI crawlers likely cannot see the majority of this page.",
  },
  "static_content.recommend.ssr": {
    title: "Server-side rendering recommended",
    description: "This page relies heavily on client-side JavaScript. Consider implementing SSR, SSG, or a pre-rendering service.",
  },
  "static_content.recommend.partial": {
    title: "Some content only visible after JS",
    description: "Certain content sections load dynamically — consider server-rendering them for AI visibility.",
  },
  "static_content.render_failed": {
    title: "Browser rendering failed",
    description: "Could not render the page with a headless browser to compare static vs. dynamic content.",
  },

  // ── Readability ─────────────────────────────────────────────────────────
  "readability.text_too_short": {
    title: "Insufficient text for analysis",
    description: "The page has too little text content to produce meaningful readability metrics.",
  },
  "readability.flesch_reading_ease.green": {
    title: "Reading ease: good",
    description: "Content reads at an accessible level suitable for AI summarization.",
  },
  "readability.flesch_reading_ease.yellow": {
    title: "Reading ease: moderate",
    description: "Content is somewhat complex — simpler language may improve AI summarization quality.",
  },
  "readability.flesch_reading_ease.red": {
    title: "Reading ease: difficult",
    description: "Content is highly complex — AI systems may struggle to produce accurate, concise summaries.",
  },
  "readability.flesch_kincaid_grade.green": {
    title: "Grade level: appropriate",
    description: "Content is written at a grade level suitable for broad comprehension.",
  },
  "readability.flesch_kincaid_grade.yellow": {
    title: "Grade level: high",
    description: "Content reads at a college or advanced level — consider simplifying for broader AI audiences.",
  },
  "readability.flesch_kincaid_grade.red": {
    title: "Grade level: very high",
    description: "Content requires advanced reading comprehension, limiting effective AI summarization.",
  },
  "readability.avg_sentence_length.green": {
    title: "Sentence length: good",
    description: "Sentences are concise enough for AI systems to parse effectively.",
  },
  "readability.avg_sentence_length.yellow": {
    title: "Sentence length: long",
    description: "Sentences tend to run long — breaking them up would improve AI extraction quality.",
  },

  // ── Grounding ───────────────────────────────────────────────────────────
  "grounding.coverage.info": {
    title: "Grounding coverage estimate",
    description: "Estimated percentage of this page's content that AI systems would select when citing it.",
  },
  "grounding.contentLength.long": {
    title: "Content may be too long for full coverage",
    description: "AI grounding systems typically select only 377–540 words from a single source. Consider splitting into more focused pages.",
  },

  // ── EEAT: Brand Signals ─────────────────────────────────────────────────
  "eeat.brand.about_page.present": {
    title: "About page found",
    description: "An About page exists, which helps establish brand trust and authority.",
  },
  "eeat.brand.about_page.missing": {
    title: "About page missing",
    description: "No About page detected — this is an important trust signal for AI systems.",
  },
  "eeat.brand.contact_page.present": {
    title: "Contact page found",
    description: "A Contact page exists, supporting brand legitimacy.",
  },
  "eeat.brand.contact_page.missing": {
    title: "Contact page missing",
    description: "No Contact page detected — this weakens trust signals.",
  },
  "eeat.brand.privacy_page.present": {
    title: "Privacy policy found",
    description: "A Privacy Policy page exists.",
  },
  "eeat.brand.privacy_page.missing": {
    title: "Privacy policy missing",
    description: "No Privacy Policy page detected — required for GDPR and a trust signal.",
  },
  "eeat.brand.terms_page.present": {
    title: "Terms of service found",
    description: "A Terms of Service page exists.",
  },
  "eeat.brand.terms_page.missing": {
    title: "Terms of service missing",
    description: "No Terms of Service page detected.",
  },
  "eeat.brand.org_schema_sameas.present": {
    title: "Organization sameAs links present",
    description: "The Organization schema includes sameAs links to authoritative profiles.",
  },
  "eeat.brand.org_schema_sameas.missing": {
    title: "Organization sameAs links missing",
    description: "No sameAs links in Organization schema — add social profiles and directory listings.",
  },
  "eeat.brand.https.present": {
    title: "HTTPS enabled",
    description: "The site uses HTTPS, a baseline security and trust signal.",
  },
  "eeat.brand.https.missing": {
    title: "HTTPS not enabled",
    description: "The site does not use HTTPS — this is a critical trust issue.",
  },
  "eeat.brand.hsts.present": {
    title: "HSTS header present",
    description: "Strict Transport Security header enforces HTTPS connections.",
  },
  "eeat.brand.hsts.missing": {
    title: "HSTS header missing",
    description: "No Strict-Transport-Security header — consider adding it to enforce HTTPS.",
  },
  "eeat.brand.csp.present": {
    title: "Content Security Policy present",
    description: "A Content-Security-Policy header is set.",
  },
  "eeat.brand.csp.missing": {
    title: "Content Security Policy missing",
    description: "No Content-Security-Policy header detected.",
  },
  "eeat.brand.robots_txt.present": {
    title: "robots.txt found",
    description: "A robots.txt file exists, guiding AI and search crawlers.",
  },
  "eeat.brand.robots_txt.missing": {
    title: "robots.txt missing",
    description: "No robots.txt file detected — AI crawlers may have no crawl guidance.",
  },

  // ── EEAT: Page Signals ──────────────────────────────────────────────────
  "eeat.page.freshness_signals.adequate": {
    title: "Freshness signals adequate",
    description: "This page has sufficient date signals for AI systems to assess content recency.",
  },
  "eeat.page.freshness_signals.weak": {
    title: "Weak freshness signals",
    description: "Limited date information — AI systems may underweight this content for recency.",
  },
  "eeat.page.author_byline.present": {
    title: "Author byline found",
    description: "An author name is attributed to this content, supporting E-E-A-T expertise signals.",
  },
  "eeat.page.author_byline.missing": {
    title: "Author byline missing",
    description: "No author attribution — adding a byline strengthens expertise and trust signals.",
  },
  "eeat.page.publication_dates.present": {
    title: "Publication dates visible",
    description: "The page displays when it was published or updated.",
  },
  "eeat.page.publication_dates.missing": {
    title: "Publication dates not visible",
    description: "No visible publication or update date — users and AI cannot assess content freshness.",
  },
  "eeat.page.publication_dates.present.info": {
    title: "Publication dates visible (informational)",
    description: "Dates were detected but are not scored for this page type.",
  },
  "eeat.page.publication_dates.missing.info": {
    title: "Publication dates not visible (informational)",
    description: "No dates detected. Not scored for this page type, but dates can still benefit content freshness signals.",
  },
  "eeat.page.external_citations.present": {
    title: "External citations found",
    description: "The page links to external sources, supporting expertise and factual credibility.",
  },
  "eeat.page.external_citations.missing": {
    title: "No external citations",
    description: "No outbound links to supporting sources — adding references strengthens credibility.",
  },
  "eeat.page.reviews_testimonials.present": {
    title: "Reviews or testimonials found",
    description: "Social proof elements are present on this page.",
  },
  "eeat.page.reviews_testimonials.missing": {
    title: "No reviews or testimonials",
    description: "No social proof detected — consider adding customer reviews or testimonials.",
  },

  // ── Structured Data ─────────────────────────────────────────────────────
  "structuredData.noJsonLd": {
    title: "No JSON-LD structured data",
    description: "No JSON-LD scripts found. AI systems rely on structured data to understand page content and type.",
  },
  "structuredData.malformed": {
    title: "Malformed JSON-LD",
    description: "JSON-LD is present but contains syntax errors — AI systems will ignore it.",
  },
  "structuredData.requiredMissing": {
    title: "Required schema missing",
    description: "The expected schema type for this page's intent is not present. See the finding details for which types to add.",
  },
  "structuredData.requiredPresent": {
    title: "Required schema present",
    description: "The expected schema type for this page's intent is correctly implemented.",
  },
  "structuredData.fieldIncomplete": {
    title: "Schema fields incomplete",
    description: "The schema is present but some recommended properties are missing. See the finding details for specific fields.",
  },
  "structuredData.additionalSuggested": {
    title: "Additional schema suggested",
    description: "An additional schema type would benefit this page but is not present. See the finding details for which types to add.",
  },
  "structuredData.dateModifiedMissing": {
    title: "dateModified missing from schema",
    description: "The schema doesn't include dateModified — AI systems use this for freshness ranking.",
  },
  "structuredData.bonusPresent": {
    title: "Bonus schema present",
    description: "Extra structured data beyond the minimum is present — this helps AI understanding.",
  },
  "structuredData.bonusMissing": {
    title: "Bonus schema opportunity",
    description: "An optional supplementary schema could further improve AI comprehension of this page.",
  },

  // ── Accessibility ───────────────────────────────────────────────────────
  "accessibility.scan_failed": {
    title: "Accessibility scan failed",
    description: "The automated accessibility check could not be completed.",
  },
  "accessibility.no_violations": {
    title: "No accessibility violations",
    description: "All scanned accessibility rules passed.",
  },
  "accessibility.image-alt": {
    title: "Images missing alt text",
    description: "Some images lack alt attributes — AI systems use alt text to understand visual content.",
  },
  "accessibility.heading-order": {
    title: "Heading hierarchy broken",
    description: "Headings skip levels (e.g., h1 to h3) — AI systems rely on heading hierarchy to understand content structure and extract sections for citation.",
  },
  "accessibility.document-title": {
    title: "Page title missing",
    description: "No <title> element found — this is a primary identifier for AI systems.",
  },
  "accessibility.html-has-lang": {
    title: "Language not declared",
    description: "The <html> element has no lang attribute — AI systems need this to process content correctly.",
  },
  "accessibility.landmark-one-main": {
    title: "No main landmark",
    description: "No <main> element — AI systems use landmarks to identify primary content.",
  },
  "accessibility.list": {
    title: "Incorrect list markup",
    description: "List items are not properly structured in <ul>, <ol>, or <dl> containers.",
  },
  "accessibility.empty-heading": {
    title: "Empty heading elements",
    description: "Heading tags contain no text, breaking content structure for AI parsers.",
  },

  // ── Citation Readiness ──────────────────────────────────────────────────
  "citation.content.insufficient": {
    title: "Insufficient content",
    description: "Too little text for AI systems to evaluate — pages need at least 50 words of substantive content.",
  },
  "citation.structure.noH1": {
    title: "Missing H1 heading",
    description: "No primary heading found — AI systems use H1 to identify the page's main topic.",
  },
  "citation.structure.noSubheadings": {
    title: "Missing subheadings",
    description: "No H2 or H3 headings found — subheadings help AI break content into citable sections.",
  },
  "citation.meta.missing": {
    title: "Meta description missing",
    description: "No meta description tag — this is often the first text AI systems see about a page.",
  },
  "citation.meta.tooShort": {
    title: "Meta description too short",
    description: "The meta description is under 120 characters — aim for 120–160 for optimal AI visibility.",
  },
  "citation.meta.tooLong": {
    title: "Meta description too long",
    description: "The meta description exceeds 160 characters and may be truncated by AI systems.",
  },
  "citation.meta.boilerplate": {
    title: "Meta description is boilerplate",
    description: "The meta description uses generic phrasing like 'Learn more about...' — write a specific, unique summary.",
  },
  "citation.meta.duplicate": {
    title: "Duplicate meta description",
    description: "This meta description is shared by multiple pages, reducing each page's distinctiveness to AI systems.",
  },
  "citation.content.thin_skipped": {
    title: "Too little content for citation evaluation",
    description: "This page has fewer than 100 words of content — not enough for a meaningful citation readiness evaluation. Add substantive content to receive a score.",
  },
  "citation.intent.skipped": {
    title: "Citation not applicable",
    description: "This page type (e.g., Authentication, Legal) is not evaluated for citation readiness.",
  },
  "citation.listing.skipped": {
    title: "Listing page — citation not applicable",
    description: "This page appears to be a collection or listing page (low text content with many internal links). Listing pages are designed for navigation, not information delivery, and are not evaluated for citation readiness.",
  },
  "citation.evaluation.failed": {
    title: "Citation evaluation failed",
    description: "The AI evaluation could not be completed for this page.",
  },
  "citation.topIssue": {
    title: "Top citation issue",
    description: "The most significant factor affecting this page's citation readiness.",
  },
  "citation.deductions.applied": {
    title: "Structural deductions applied",
    description: "Points were deducted for missing structural elements (H1, subheadings, meta description).",
  },

  // ── Browser Pipeline ────────────────────────────────────────────────────
  "browser_pipeline.browser_failed": {
    title: "Browser launch failed",
    description: "Could not launch a headless browser for this page — screenshot and JS-rendering checks were skipped.",
  },
};

// ─── Resolver function ──────────────────────────────────────────────────────

/**
 * Resolve a human-readable title and description for a finding.
 *
 * Lookup order:
 *   1. Exact match on `issueKey` in the registry
 *   2. `context.criterion` (LLM-generated label, used by citation readiness)
 *   3. `subcategory` with underscores → spaces and title-cased
 *   4. `issueKey` cleaned up as a last resort
 *
 * For description, falls back to `context.rationale`, `context.message`,
 * or `context.topIssue`.
 */
export function resolveFindingLabel(finding: {
  issueKey?: string;
  subcategory?: string;
  context?: Record<string, any>;
}): FindingLabel {
  // 1. Exact registry match
  if (finding.issueKey && FINDING_LABELS[finding.issueKey]) {
    const registered = FINDING_LABELS[finding.issueKey];
    // Use the registry description, but prefer a dynamic context message if available
    const description =
      finding.context?.rationale ||
      finding.context?.message ||
      finding.context?.topIssue ||
      registered.description;
    return { title: registered.title, description };
  }

  // 2. context.criterion (LLM-generated, e.g. "Factual Accuracy")
  const title =
    finding.context?.criterion ||
    formatSubcategory(finding.subcategory) ||
    formatIssueKey(finding.issueKey);

  const description =
    finding.context?.rationale ||
    finding.context?.message ||
    finding.context?.topIssue ||
    finding.context?.error ||
    "";

  return { title, description };
}

function formatSubcategory(subcategory?: string): string {
  if (!subcategory) return "";
  return subcategory
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatIssueKey(issueKey?: string): string {
  if (!issueKey) return "Unknown";
  // Take the last segment after the last dot, clean up
  const lastSegment = issueKey.split(".").pop() || issueKey;
  return lastSegment
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
